
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Activity,
  Clock,
  Target,
  StickyNote,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';

interface Shot {
  id: string;
  playerId: string;
  playerName: string;
  distance: number;
  quality: 'excellent' | 'good' | 'needs_work';
  shotType: string;
  timestamp: Date;
  notes?: string;
}

interface ShotStreamProps {
  shots: Shot[];
  onRefresh?: () => void;
  onFilter?: (filters: any) => void;
}

export function ShotStream({ shots, onRefresh, onFilter }: ShotStreamProps) {
  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent':
        return 'bg-green-500 text-white';
      case 'good':
        return 'bg-yellow-500 text-white';
      case 'needs_work':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getQualityIcon = (quality: string) => {
    switch (quality) {
      case 'excellent':
        return '🏆';
      case 'good':
        return '👍';
      case 'needs_work':
        return '⚠️';
      default:
        return '🏏';
    }
  };

  const getShotTypeColor = (shotType: string) => {
    const colors = {
      'Drive': 'bg-blue-100 text-blue-800',
      'Cut': 'bg-purple-100 text-purple-800',
      'Pull': 'bg-green-100 text-green-800',
      'Hook': 'bg-orange-100 text-orange-800',
      'Sweep': 'bg-pink-100 text-pink-800',
      'Flick': 'bg-indigo-100 text-indigo-800',
      'Defensive': 'bg-gray-100 text-gray-800',
    };
    return colors[shotType as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatTime = (timestamp: Date) => {
    return formatDistanceToNow(timestamp, { addSuffix: true });
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Activity className="w-5 h-5 mr-2" />
              Live Shot Stream
            </CardTitle>
            <CardDescription>
              Real-time updates from all players ({shots.length} recent shots)
            </CardDescription>
          </div>
          <div className="flex space-x-2">
            {onFilter && (
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
            )}
            {onRefresh && (
              <Button variant="outline" size="sm" onClick={onRefresh}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        <ScrollArea className="h-96">
          <div className="space-y-1 p-4">
            {shots.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No shots recorded yet</p>
                <p className="text-sm">Shots will appear here in real-time</p>
              </div>
            ) : (
              shots.map((shot, index) => (
                <div
                  key={shot.id}
                  className={`p-3 rounded-lg border transition-all duration-200 hover:bg-gray-50 ${
                    index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{getQualityIcon(shot.quality)}</span>
                        <span className="font-medium text-sm">{shot.playerName}</span>
                        <Badge className={`text-xs ${getQualityColor(shot.quality)}`}>
                          {shot.quality.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className={`text-xs ${getShotTypeColor(shot.shotType)}`}>
                          {shot.shotType}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center">
                          <Target className="w-3 h-3 mr-1" />
                          {shot.distance}m
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatTime(shot.timestamp)}
                        </span>
                      </div>
                      
                      {shot.notes && (
                        <div className="flex items-start space-x-2 mt-2 p-2 bg-gray-50 rounded text-xs">
                          <StickyNote className="w-3 h-3 mt-0.5 text-gray-500" />
                          <span className="text-gray-700">{shot.notes}</span>
                        </div>
                      )}
                    </div>
                    
                    {index === 0 && (
                      <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 border-blue-200">
                        Latest
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}